/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createEffect, createSignal } from 'davaux/client'
import type { StyleObject, StyleProps, UiSpacing } from './types.js'
import { cx, mergeStyle, pickStyleProps, spacing } from './types.js'

export interface DataTableColumn {
  key: string
  label: string
  sortable?: boolean
  /** Text alignment for this column's cells. */
  align?: 'left' | 'center' | 'right'
  /** Fixed width as a pixel number or CSS string. Most useful with `layout="fixed"`. */
  width?: number | string
}

export interface DataTableProps extends StyleProps {
  columns: DataTableColumn[]
  data?: Record<string, unknown>[]
  /** Column keys to include in text filter. Defaults to all columns. */
  filterKeys?: string[]
  /** Rows per page. 0 disables pagination. Default: 10 */
  rowsPerPage?: number
  /** Alternating row background colors; `true` defaults to `'odd'`. */
  striped?: boolean | 'odd' | 'even'
  highlightOnHover?: boolean
  caption?: string
  /** Which edge the caption renders on. Default: 'bottom' */
  captionSide?: 'top' | 'bottom'
  /** Adds a border around the entire table. */
  withTableBorder?: boolean
  /** Adds vertical borders between columns. */
  withColumnBorders?: boolean
  /** Shows horizontal borders between rows. Default: true */
  withRowBorders?: boolean
  /** CSS color value applied to all table border lines. */
  borderColor?: string
  /** Padding applied to the left and right of each cell. */
  horizontalSpacing?: UiSpacing
  /** Padding applied to the top and bottom of each cell. */
  verticalSpacing?: UiSpacing
  /** CSS `table-layout` algorithm: `'auto'` sizes columns by content, `'fixed'` by column widths. */
  layout?: 'auto' | 'fixed'
  /** Enables row selection checkboxes. */
  selectable?: boolean
  /** Row object key used as a unique row identifier. Defaults to row object reference. */
  rowKey?: string
  /** Placeholder shown when no rows match the filter. */
  emptyLabel?: string
  /** Show a text filter input. Default: true */
  withFilter?: boolean
  class?: string
  style?: string | Record<string, string>
  [key: string]: unknown
}

const SORT_ASC = '↑'
const SORT_DESC = '↓'
const SORT_NONE = '↕'

function compare(a: unknown, b: unknown): number {
  if (a === null || a === undefined) return -1
  if (b === null || b === undefined) return 1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

function DataTable({
  columns,
  data = [],
  filterKeys,
  rowsPerPage = 10,
  striped = false,
  highlightOnHover = false,
  caption,
  captionSide,
  withTableBorder,
  withColumnBorders,
  withRowBorders = true,
  borderColor,
  horizontalSpacing,
  verticalSpacing,
  layout,
  selectable = false,
  rowKey,
  emptyLabel = 'No results found',
  withFilter = true,
  class: className,
  style,
  ...rest
}: DataTableProps) {
  const [filter, setFilter] = createSignal('')
  const [sortKey, setSortKey] = createSignal<string | null>(null)
  const [sortDir, setSortDir] = createSignal<'asc' | 'desc'>('asc')
  const [page, setPage] = createSignal(1)
  const [selected, setSelected] = createSignal<unknown[]>([])

  let selectAllRef: HTMLInputElement | undefined
  let rootEl: HTMLElement | undefined

  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)

  const tableCssVars: StyleObject = {}
  if (horizontalSpacing !== undefined) tableCssVars['--dv-table-hz-spacing'] = spacing(horizontalSpacing) ?? ''
  if (verticalSpacing !== undefined) tableCssVars['--dv-table-v-spacing'] = spacing(verticalSpacing) ?? ''
  if (borderColor !== undefined) tableCssVars['--dv-table-border-color'] = borderColor
  if (captionSide !== undefined) tableCssVars['--dv-table-caption-side'] = captionSide

  const searchKeys = filterKeys ?? columns.map((c) => c.key)
  const paginate = rowsPerPage > 0
  const colCount = () => columns.length + (selectable ? 1 : 0)

  const keyOf = (row: Record<string, unknown>): unknown =>
    rowKey ? row[rowKey] : row

  const selectedSet = () => new Set(selected())

  const filtered = () => {
    const q = filter().toLowerCase().trim()
    if (!q) return data
    return data.filter((row) =>
      searchKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(q)),
    )
  }

  const sorted = () => {
    const key = sortKey()
    const dir = sortDir()
    const rows = filtered()
    if (!key) return rows
    return [...rows].sort((a, b) => {
      const cmp = compare(a[key], b[key])
      return dir === 'asc' ? cmp : -cmp
    })
  }

  const totalRows = () => filtered().length
  const totalPages = () => (paginate ? Math.max(1, Math.ceil(totalRows() / rowsPerPage)) : 1)

  const pageRows = () => {
    const rows = sorted()
    if (!paginate) return rows
    const start = (page() - 1) * rowsPerPage
    return rows.slice(start, start + rowsPerPage)
  }

  function toggleSort(key: string) {
    if (sortKey() === key) {
      setSortDir(sortDir() === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  function handleFilter(e: Event) {
    setFilter((e.target as HTMLInputElement).value)
    setPage(1)
  }

  function goPage(n: number) {
    setPage(Math.max(1, Math.min(n, totalPages())))
  }

  const pageNumbers = () => {
    const total = totalPages()
    const cur = page()
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
    const pages: (number | null)[] = [1]
    if (cur > 3) pages.push(null)
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i)
    if (cur < total - 2) pages.push(null)
    pages.push(total)
    return pages
  }

  function dispatchSelect(sel: unknown[]) {
    if (!rootEl) return
    const selectedRows = data.filter((row) => sel.includes(keyOf(row)))
    rootEl.dispatchEvent(
      new CustomEvent('dv-row-select', {
        detail: { keys: sel, rows: selectedRows },
        bubbles: true,
      }),
    )
  }

  function toggleRow(row: Record<string, unknown>) {
    const key = keyOf(row)
    const cur = selected()
    const next = cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]
    setSelected(next)
    dispatchSelect(next)
  }

  function toggleAll() {
    const filteredKeys = filtered().map(keyOf)
    const sel = selectedSet()
    const allSelected = filteredKeys.length > 0 && filteredKeys.every((k) => sel.has(k))
    const next = allSelected
      ? selected().filter((k) => !filteredKeys.includes(k))
      : [...new Set([...selected(), ...filteredKeys])]
    setSelected(next)
    dispatchSelect(next)
  }

  if (selectable) {
    createEffect(() => {
      if (!selectAllRef) return
      const filteredKeys = filtered().map(keyOf)
      const sel = selectedSet()
      const count = filteredKeys.filter((k) => sel.has(k)).length
      selectAllRef.indeterminate = count > 0 && count < filteredKeys.length
    })
  }

  return (
    <div
      ref={(el: HTMLElement) => { rootEl = el }}
      class={cx('dv-data-table', className)}
      style={mergeStyle(styleObj, style)}
      {...ownRest}
    >
      {withFilter && (
        <div class='dv-data-table-toolbar'>
          <input
            class='dv-data-table-filter'
            type='search'
            placeholder='Filter…'
            value={() => filter()}
            onInput={handleFilter}
            aria-label='Filter rows'
          />
          <span class='dv-data-table-count'>
            {() => {
              const total = totalRows()
              const sel = selectable ? selected().length : 0
              if (sel > 0) return `${sel} of ${total} selected`
              return `${total} row${total === 1 ? '' : 's'}`
            }}
          </span>
        </div>
      )}
      <div class='dv-data-table-scroll'>
        <table
          class='dv-table'
          style={Object.keys(tableCssVars).length ? mergeStyle(tableCssVars, undefined) : undefined}
          data-striped={striped ? (striped === true ? 'odd' : striped) : undefined}
          data-hover={highlightOnHover || undefined}
          data-with-table-border={withTableBorder || undefined}
          data-with-column-borders={withColumnBorders || undefined}
          data-without-row-borders={!withRowBorders || undefined}
          data-layout={layout}
        >
          {caption && <caption class='dv-table-caption'>{caption}</caption>}
          <thead class='dv-table-thead'>
            <tr class='dv-table-tr'>
              {selectable && (
                <th class='dv-table-th dv-data-table-th-select'>
                  <input
                    type='checkbox'
                    ref={(el: HTMLInputElement) => { selectAllRef = el }}
                    checked={() => {
                      const filteredKeys = filtered().map(keyOf)
                      const sel = selectedSet()
                      return filteredKeys.length > 0 && filteredKeys.every((k) => sel.has(k))
                    }}
                    onChange={toggleAll}
                    aria-label='Select all rows'
                  />
                </th>
              )}
              {columns.map((col) => {
                const isSorted = () => sortKey() === col.key
                const sortIcon = () =>
                  isSorted() ? (sortDir() === 'asc' ? SORT_ASC : SORT_DESC) : SORT_NONE
                return (
                  <th
                    class={`dv-table-th${col.sortable ? ' dv-data-table-th-sortable' : ''}`}
                    data-sorted={isSorted() || undefined}
                    style={[
                      col.align ? `text-align: ${col.align}` : '',
                      col.width ? `width: ${typeof col.width === 'number' ? `${col.width}px` : col.width}` : '',
                    ].filter(Boolean).join('; ') || undefined}
                    onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                    aria-sort={
                      isSorted()
                        ? sortDir() === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {col.label}
                    {col.sortable && (
                      <span class='dv-data-table-sort-icon' aria-hidden='true'>
                        {() => sortIcon()}
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody class='dv-table-tbody'>
            {() => {
              const rows = pageRows()
              if (rows.length === 0) {
                return (
                  <tr class='dv-table-tr'>
                    <td
                      class='dv-table-td dv-data-table-empty'
                      colSpan={colCount()}
                    >
                      {emptyLabel}
                    </td>
                  </tr>
                )
              }
              return rows.map((row) => (
                <tr
                  class='dv-table-tr'
                  data-selected={() => selectedSet().has(keyOf(row)) || undefined}
                >
                  {selectable && (
                    <td class='dv-table-td dv-data-table-td-select'>
                      <input
                        type='checkbox'
                        checked={() => selectedSet().has(keyOf(row))}
                        onChange={() => toggleRow(row)}
                        aria-label='Select row'
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      class='dv-table-td'
                      style={col.align ? `text-align: ${col.align}` : undefined}
                    >
                      {String(row[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))
            }}
          </tbody>
        </table>
      </div>
      {paginate && (
        <div class='dv-data-table-pagination'>
          <span class='dv-data-table-pagination-info'>
            {() => {
              const start = totalRows() === 0 ? 0 : (page() - 1) * rowsPerPage + 1
              const end = Math.min(page() * rowsPerPage, totalRows())
              return `${start}–${end} of ${totalRows()}`
            }}
          </span>
          <div class='dv-data-table-pagination-controls'>
            <button
              type='button'
              class='dv-data-table-page-btn'
              disabled={() => page() === 1}
              onClick={() => goPage(page() - 1)}
              aria-label='Previous page'
            >
              ‹
            </button>
            {() =>
              pageNumbers().map((n) =>
                n === null ? (
                  <span class='dv-data-table-page-ellipsis'>…</span>
                ) : (
                  <button
                    type='button'
                    class='dv-data-table-page-btn'
                    data-current={n === page() || undefined}
                    aria-current={n === page() ? 'page' : undefined}
                    onClick={() => goPage(n as number)}
                  >
                    {n}
                  </button>
                ),
              )
            }
            <button
              type='button'
              class='dv-data-table-page-btn'
              disabled={() => page() === totalPages()}
              onClick={() => goPage(page() + 1)}
              aria-label='Next page'
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default reactive(DataTable)
